import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout';
import { Plus, Edit2, Trash2, Eye, EyeOff, ExternalLink, Save, X, ChevronDown, ChevronUp, LogOut, Mic, Video, Presentation, Users, Settings } from 'lucide-react';
import axios from 'axios';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['Transformation', 'Leadership', 'Operating Models', 'Delivery', 'Culture', 'Strategy'];
const MEDIA_TYPES = ['Interview', 'Speaking', 'Press', 'Podcast'];

// Create axios instance with auth header
const createAuthAxios = (token) => {
  return axios.create({
    baseURL: API,
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
};

const generateSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', { 
    day: 'numeric',
    month: 'short', 
    year: 'numeric' 
  });
};

// Login Component
const LoginForm = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      // Store token for future requests
      onLogin({ token: response.data.token, username: response.data.username });
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#faf9f7]">
      <div className="bg-white p-8 border border-[#e5e2dc] w-full max-w-md">
        <h1 className="font-playfair text-2xl font-bold text-[#1a1a1a] mb-2 text-center">
          Admin Login
        </h1>
        <p className="text-[#4a4744] text-sm text-center mb-8">
          Enter your credentials to access the admin panel
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans text-[#1a1a1a] focus:outline-none focus:border-[#d4782c]"
              required
              data-testid="login-username"
            />
          </div>

          <div>
            <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans text-[#1a1a1a] focus:outline-none focus:border-[#d4782c]"
              required
              data-testid="login-password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full btn-primary"
            data-testid="login-submit"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Article Form Component
const ArticleForm = ({ article, onSave, onCancel, isNew, auth }) => {
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'Transformation',
    author: 'Nadir Khan',
    publication_date: new Date().toISOString().split('T')[0],
    linkedin_url: '',
    published: true,
    featured: false,
    display_order: 0,
    ...article
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [autoSlug, setAutoSlug] = useState(isNew);

  const authAxios = createAuthAxios(auth.token);

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'title' && autoSlug) {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        linkedin_url: formData.linkedin_url || null
      };

      if (isNew) {
        await authAxios.post('/articles', payload);
      } else {
        await authAxios.put(`/articles/${article.slug}`, payload);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8">
      <div className="bg-white w-full max-w-4xl mx-4 my-auto" data-testid="article-form-modal">
        <div className="border-b border-[#e5e2dc] px-6 py-4 flex items-center justify-between">
          <h2 className="font-playfair text-xl font-bold text-[#1a1a1a]">
            {isNew ? 'New Article' : 'Edit Article'}
          </h2>
          <button onClick={onCancel} className="text-[#4a4744] hover:text-[#1a1a1a]">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">{error}</div>
          )}

          <div>
            <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c]"
              required
              data-testid="article-title-input"
            />
          </div>

          <div>
            <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">
              URL Slug * {isNew && <button type="button" onClick={() => setAutoSlug(!autoSlug)} className="ml-2 text-xs text-[#d4782c]">({autoSlug ? 'auto' : 'manual'})</button>}
            </label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => { setAutoSlug(false); handleChange('slug', e.target.value); }}
              className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c]"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">Category</label>
              <select
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c]"
              >
                {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">Publication Date</label>
              <input
                type="date"
                value={formData.publication_date}
                onChange={(e) => handleChange('publication_date', e.target.value)}
                className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c]"
              />
            </div>
            <div>
              <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">Display Order</label>
              <input
                type="number"
                value={formData.display_order}
                onChange={(e) => handleChange('display_order', parseInt(e.target.value) || 0)}
                className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c]"
              />
              <p className="mt-1 text-xs text-[#4a4744]">Lower numbers appear first</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">Excerpt *</label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => handleChange('excerpt', e.target.value)}
              className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c] resize-none"
              rows={3}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">Content * (HTML)</label>
            <textarea
              value={formData.content}
              onChange={(e) => handleChange('content', e.target.value)}
              className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c] resize-none font-mono text-sm"
              rows={10}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">LinkedIn URL (optional)</label>
            <input
              type="url"
              value={formData.linkedin_url || ''}
              onChange={(e) => handleChange('linkedin_url', e.target.value)}
              className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c]"
            />
          </div>

          <div className="flex items-center gap-8">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={formData.published} onChange={(e) => handleChange('published', e.target.checked)} className="w-5 h-5" />
              <span className="font-dm-sans text-sm">Published</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={formData.featured} onChange={(e) => handleChange('featured', e.target.checked)} className="w-5 h-5" />
              <span className="font-dm-sans text-sm">Featured</span>
            </label>
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t border-[#e5e2dc]">
            <button type="button" onClick={onCancel} className="px-6 py-3 text-[#4a4744] hover:text-[#1a1a1a]">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
              <Save size={18} />
              {saving ? 'Saving...' : (isNew ? 'Create' : 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Media Form Component
const MediaForm = ({ media, onSave, onCancel, isNew, auth }) => {
  const [formData, setFormData] = useState({
    title: '',
    source: '',
    media_type: 'Interview',
    link_url: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    published: true,
    display_order: 0,
    ...media
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const authAxios = createAuthAxios(auth.token);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (isNew) {
        await authAxios.post('/media', formData);
      } else {
        await authAxios.put(`/media/${media.id}`, formData);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save media item');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl" data-testid="media-form-modal">
        <div className="border-b border-[#e5e2dc] px-6 py-4 flex items-center justify-between">
          <h2 className="font-playfair text-xl font-bold text-[#1a1a1a]">
            {isNew ? 'New Media Item' : 'Edit Media Item'}
          </h2>
          <button onClick={onCancel} className="text-[#4a4744] hover:text-[#1a1a1a]">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">{error}</div>
          )}

          <div>
            <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c]"
              placeholder="e.g., Why Organisation Transformation Programs Fail"
              required
              data-testid="media-title-input"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">Source / Publication *</label>
              <input
                type="text"
                value={formData.source}
                onChange={(e) => handleChange('source', e.target.value)}
                className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c]"
                placeholder="e.g., Australian Business Journal"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">Type *</label>
              <select
                value={formData.media_type}
                onChange={(e) => handleChange('media_type', e.target.value)}
                className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c]"
              >
                {MEDIA_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">Link URL *</label>
            <input
              type="url"
              value={formData.link_url}
              onChange={(e) => handleChange('link_url', e.target.value)}
              className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c]"
              placeholder="https://..."
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">Date *</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c]"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">Display Order</label>
              <input
                type="number"
                value={formData.display_order}
                onChange={(e) => handleChange('display_order', parseInt(e.target.value) || 0)}
                className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c]"
              />
              <p className="mt-1 text-xs text-[#4a4744]">Lower numbers appear first</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">Description</label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
              className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c] resize-none"
              rows={3}
              placeholder="Brief description of the content..."
            />
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={formData.published} onChange={(e) => handleChange('published', e.target.checked)} className="w-5 h-5" />
              <span className="font-dm-sans text-sm">Published</span>
            </label>
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t border-[#e5e2dc]">
            <button type="button" onClick={onCancel} className="px-6 py-3 text-[#4a4744] hover:text-[#1a1a1a]">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
              <Save size={18} />
              {saving ? 'Saving...' : (isNew ? 'Create' : 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Articles Tab Component
const ArticlesTab = ({ auth }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);

  const authAxios = createAuthAxios(auth.token);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authAxios.get('/admin/articles');
      setArticles(response.data);
    } catch (err) {
      logger.error('Error fetching articles:', err);
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const newArticles = [...articles];
    [newArticles[index - 1], newArticles[index]] = [newArticles[index], newArticles[index - 1]];
    
    const reorderData = newArticles.map((a, i) => ({ slug: a.slug, display_order: i }));
    try {
      await authAxios.post('/admin/articles/reorder', { items: reorderData });
      setArticles(newArticles);
    } catch (err) {
      logger.error('Error reordering:', err);
    }
  };

  const handleMoveDown = async (index) => {
    if (index === articles.length - 1) return;
    const newArticles = [...articles];
    [newArticles[index], newArticles[index + 1]] = [newArticles[index + 1], newArticles[index]];
    
    const reorderData = newArticles.map((a, i) => ({ slug: a.slug, display_order: i }));
    try {
      await authAxios.post('/admin/articles/reorder', { items: reorderData });
      setArticles(newArticles);
    } catch (err) {
      logger.error('Error reordering:', err);
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    try {
      await authAxios.delete(`/articles/${slug}`);
      fetchArticles();
    } catch (err) {
      logger.error('Error deleting:', err);
    }
  };

  const handleTogglePublish = async (article) => {
    try {
      await authAxios.put(`/articles/${article.slug}`, { published: !article.published });
      fetchArticles();
    } catch (err) {
      logger.error('Error toggling publish:', err);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-playfair text-2xl font-bold text-[#1a1a1a]">Articles</h2>
        <button onClick={() => { setEditingArticle(null); setShowForm(true); }} className="btn-primary flex items-center gap-2">
          <Plus size={18} /> New Article
        </button>
      </div>

      <div className="bg-white border border-[#e5e2dc] overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[#faf9f7] border-b border-[#e5e2dc]">
            <tr>
              <th className="px-4 py-3 text-left font-dm-sans font-semibold text-sm text-[#1a1a1a]">Order</th>
              <th className="px-4 py-3 text-left font-dm-sans font-semibold text-sm text-[#1a1a1a]">Title</th>
              <th className="px-4 py-3 text-left font-dm-sans font-semibold text-sm text-[#1a1a1a]">Category</th>
              <th className="px-4 py-3 text-left font-dm-sans font-semibold text-sm text-[#1a1a1a]">Date</th>
              <th className="px-4 py-3 text-left font-dm-sans font-semibold text-sm text-[#1a1a1a]">Status</th>
              <th className="px-4 py-3 text-left font-dm-sans font-semibold text-sm text-[#1a1a1a]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-[#4a4744]">Loading...</td></tr>
            ) : articles.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-[#4a4744]">No articles yet.</td></tr>
            ) : (
              articles.map((article, index) => (
                <tr key={article.id} className="border-b border-[#e5e2dc] hover:bg-[#faf9f7]">
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <button onClick={() => handleMoveUp(index)} disabled={index === 0} className="p-1 text-[#4a4744] hover:text-[#d4782c] disabled:opacity-30">
                        <ChevronUp size={16} />
                      </button>
                      <button onClick={() => handleMoveDown(index)} disabled={index === articles.length - 1} className="p-1 text-[#4a4744] hover:text-[#d4782c] disabled:opacity-30">
                        <ChevronDown size={16} />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-dm-sans font-medium text-[#1a1a1a]">{article.title}</p>
                    <p className="text-xs text-[#4a4744]">/insights/{article.slug}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-[#faf9f7] text-[#4a4744] text-xs">{article.category}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#4a4744]">{formatDate(article.publication_date)}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleTogglePublish(article)} className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium ${article.published ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {article.published ? <Eye size={12} /> : <EyeOff size={12} />}
                      {article.published ? 'Published' : 'Draft'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <a href={`/insights/${article.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-[#4a4744] hover:text-[#d4782c]"><ExternalLink size={16} /></a>
                      <button onClick={() => { setEditingArticle(article); setShowForm(true); }} className="p-2 text-[#4a4744] hover:text-[#d4782c]"><Edit2 size={16} /></button>
                      <button onClick={() => handleDelete(article.slug)} className="p-2 text-[#4a4744] hover:text-red-600"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-sm text-[#4a4744]">
        Total: {articles.length} | Published: {articles.filter(a => a.published).length} | Drafts: {articles.filter(a => !a.published).length}
      </div>

      {showForm && (
        <ArticleForm
          article={editingArticle}
          onSave={() => { setShowForm(false); setEditingArticle(null); fetchArticles(); }}
          onCancel={() => { setShowForm(false); setEditingArticle(null); }}
          isNew={!editingArticle}
          auth={auth}
        />
      )}
    </div>
  );
};

// Media Tab Component
const MediaTab = ({ auth }) => {
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMedia, setEditingMedia] = useState(null);

  const authAxios = createAuthAxios(auth.token);

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authAxios.get('/admin/media');
      setMediaItems(response.data);
    } catch (err) {
      logger.error('Error fetching media:', err);
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const newItems = [...mediaItems];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    
    const reorderData = newItems.map((m, i) => ({ id: m.id, display_order: i }));
    try {
      await authAxios.post('/admin/media/reorder', { items: reorderData });
      setMediaItems(newItems);
    } catch (err) {
      logger.error('Error reordering:', err);
    }
  };

  const handleMoveDown = async (index) => {
    if (index === mediaItems.length - 1) return;
    const newItems = [...mediaItems];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    
    const reorderData = newItems.map((m, i) => ({ id: m.id, display_order: i }));
    try {
      await authAxios.post('/admin/media/reorder', { items: reorderData });
      setMediaItems(newItems);
    } catch (err) {
      logger.error('Error reordering:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this media item?')) return;
    try {
      await authAxios.delete(`/media/${id}`);
      fetchMedia();
    } catch (err) {
      logger.error('Error deleting:', err);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Interview': return <Video size={16} />;
      case 'Speaking': return <Presentation size={16} />;
      case 'Podcast': return <Mic size={16} />;
      default: return <ExternalLink size={16} />;
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-playfair text-2xl font-bold text-[#1a1a1a]">Media & Speaking</h2>
        <button onClick={() => { setEditingMedia(null); setShowForm(true); }} className="btn-primary flex items-center gap-2">
          <Plus size={18} /> New Media Item
        </button>
      </div>

      <div className="bg-white border border-[#e5e2dc] overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[#faf9f7] border-b border-[#e5e2dc]">
            <tr>
              <th className="px-4 py-3 text-left font-dm-sans font-semibold text-sm text-[#1a1a1a]">Order</th>
              <th className="px-4 py-3 text-left font-dm-sans font-semibold text-sm text-[#1a1a1a]">Title</th>
              <th className="px-4 py-3 text-left font-dm-sans font-semibold text-sm text-[#1a1a1a]">Type</th>
              <th className="px-4 py-3 text-left font-dm-sans font-semibold text-sm text-[#1a1a1a]">Source</th>
              <th className="px-4 py-3 text-left font-dm-sans font-semibold text-sm text-[#1a1a1a]">Date</th>
              <th className="px-4 py-3 text-left font-dm-sans font-semibold text-sm text-[#1a1a1a]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-[#4a4744]">Loading...</td></tr>
            ) : mediaItems.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-[#4a4744]">No media items yet.</td></tr>
            ) : (
              mediaItems.map((item, index) => (
                <tr key={item.id} className="border-b border-[#e5e2dc] hover:bg-[#faf9f7]">
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <button onClick={() => handleMoveUp(index)} disabled={index === 0} className="p-1 text-[#4a4744] hover:text-[#d4782c] disabled:opacity-30">
                        <ChevronUp size={16} />
                      </button>
                      <button onClick={() => handleMoveDown(index)} disabled={index === mediaItems.length - 1} className="p-1 text-[#4a4744] hover:text-[#d4782c] disabled:opacity-30">
                        <ChevronDown size={16} />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-dm-sans font-medium text-[#1a1a1a]">{item.title}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#faf9f7] text-[#d4782c] text-xs">
                      {getTypeIcon(item.media_type)} {item.media_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#4a4744]">{item.source}</td>
                  <td className="px-4 py-3 text-sm text-[#4a4744]">{formatDate(item.date)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <a href={item.link_url} target="_blank" rel="noopener noreferrer" className="p-2 text-[#4a4744] hover:text-[#d4782c]"><ExternalLink size={16} /></a>
                      <button onClick={() => { setEditingMedia(item); setShowForm(true); }} className="p-2 text-[#4a4744] hover:text-[#d4782c]"><Edit2 size={16} /></button>
                      <button onClick={() => handleDelete(item.id)} className="p-2 text-[#4a4744] hover:text-red-600"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-sm text-[#4a4744]">
        Total: {mediaItems.length} items
      </div>

      {showForm && (
        <MediaForm
          media={editingMedia}
          onSave={() => { setShowForm(false); setEditingMedia(null); fetchMedia(); }}
          onCancel={() => { setShowForm(false); setEditingMedia(null); }}
          isNew={!editingMedia}
          auth={auth}
        />
      )}
    </div>
  );
};

// Team Member Form Component
const TeamMemberForm = ({ member, onSave, onCancel, auth }) => {
  const [formData, setFormData] = useState({
    name: member?.name || '',
    role: member?.role || '',
    photo_url: member?.photo_url || '',
    summary: member?.summary || '',
    full_bio: member?.full_bio || '',
    display_order: member?.display_order || 0,
    published: member?.published ?? true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const authAxios = createAuthAxios(auth.token);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      await authAxios.put(`/team/${member.id}`, formData);
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save team member');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8">
      <div className="bg-white w-full max-w-4xl mx-4 my-auto" data-testid="team-member-form-modal">
        <div className="border-b border-[#e5e2dc] px-6 py-4 flex items-center justify-between">
          <h2 className="font-playfair text-xl font-bold text-[#1a1a1a]">
            Edit Team Member
          </h2>
          <button onClick={onCancel} className="text-[#4a4744] hover:text-[#1a1a1a]">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">{error}</div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c]"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">Role</label>
              <input
                type="text"
                value={formData.role}
                onChange={(e) => handleChange('role', e.target.value)}
                className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c]"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">Photo URL</label>
            <input
              type="url"
              value={formData.photo_url}
              onChange={(e) => handleChange('photo_url', e.target.value)}
              className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c]"
            />
            {formData.photo_url && (
              <div className="mt-2">
                <img src={formData.photo_url} alt="Preview" className="w-24 h-24 object-cover grayscale" />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">Summary (Card Text)</label>
            <textarea
              value={formData.summary}
              onChange={(e) => handleChange('summary', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c]"
              placeholder="Brief summary shown on the card..."
              required
            />
            <p className="text-xs text-[#4a4744] mt-1">{formData.summary.length} characters</p>
          </div>

          <div>
            <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">Full Bio (Modal Text)</label>
            <textarea
              value={formData.full_bio}
              onChange={(e) => handleChange('full_bio', e.target.value)}
              rows={10}
              className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c]"
              placeholder="Full biography shown in the modal. Use double line breaks for paragraphs."
              required
            />
            <p className="text-xs text-[#4a4744] mt-1">Use blank lines to separate paragraphs. {formData.full_bio.length} characters</p>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.published}
                onChange={(e) => handleChange('published', e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm font-dm-sans text-[#1a1a1a]">Published</span>
            </label>
          </div>

          <div className="flex gap-4 pt-4 border-t border-[#e5e2dc]">
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex items-center gap-2"
            >
              <Save size={18} />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 border border-[#e5e2dc] text-[#4a4744] hover:border-[#1a1a1a] hover:text-[#1a1a1a] transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Team Tab Component
const TeamTab = ({ auth }) => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMember, setEditingMember] = useState(null);

  const authAxios = createAuthAxios(auth.token);

  const fetchTeam = useCallback(async () => {
    try {
      const response = await authAxios.get('/team/all');
      setTeamMembers(response.data);
    } catch (err) {
      logger.error('Failed to fetch team:', err);
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  const handleEdit = (member) => {
    setEditingMember(member);
  };

  if (loading) {
    return <div className="text-[#4a4744]">Loading team members...</div>;
  }

  const founder = teamMembers.find(m => m.member_type === 'founder');
  const consultants = teamMembers.filter(m => m.member_type === 'consultant');

  return (
    <div data-testid="team-tab">
      <div className="mb-8">
        <p className="text-[#4a4744] text-sm">
          Edit the summary and bio text for team members displayed on the Our People page.
        </p>
      </div>

      {/* Founder Section */}
      {founder && (
        <div className="mb-8">
          <h3 className="font-playfair text-lg font-bold text-[#1a1a1a] mb-4">Founder</h3>
          <div className="bg-white border border-[#e5e2dc] p-6">
            <div className="flex items-start gap-6">
              <img 
                src={founder.photo_url} 
                alt={founder.name} 
                className="w-20 h-20 object-cover grayscale"
              />
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-playfair text-xl font-bold text-[#1a1a1a]">{founder.name}</h4>
                    <p className="text-[#d4782c] font-dm-sans text-sm">{founder.role}</p>
                  </div>
                  <button
                    onClick={() => handleEdit(founder)}
                    className="p-2 text-[#4a4744] hover:text-[#d4782c] border border-[#e5e2dc] hover:border-[#d4782c]"
                    data-testid="edit-founder-btn"
                  >
                    <Edit2 size={16} />
                  </button>
                </div>
                <p className="text-[#4a4744] text-sm mt-3 line-clamp-2">{founder.summary}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Consultants Section */}
      <div>
        <h3 className="font-playfair text-lg font-bold text-[#1a1a1a] mb-4">Consultants</h3>
        <div className="space-y-4">
          {consultants.map((consultant) => (
            <div key={consultant.id} className="bg-white border border-[#e5e2dc] p-6">
              <div className="flex items-start gap-6">
                <img 
                  src={consultant.photo_url} 
                  alt={consultant.name} 
                  className="w-16 h-16 object-cover grayscale"
                />
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-playfair text-lg font-bold text-[#1a1a1a]">{consultant.name}</h4>
                      <p className="text-[#d4782c] font-dm-sans text-sm">{consultant.role}</p>
                    </div>
                    <button
                      onClick={() => handleEdit(consultant)}
                      className="p-2 text-[#4a4744] hover:text-[#d4782c] border border-[#e5e2dc] hover:border-[#d4782c]"
                      data-testid={`edit-${consultant.id}-btn`}
                    >
                      <Edit2 size={16} />
                    </button>
                  </div>
                  <p className="text-[#4a4744] text-sm mt-2 line-clamp-2">{consultant.summary}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 text-sm text-[#4a4744]">
        Total: {teamMembers.length} team members
      </div>

      {editingMember && (
        <TeamMemberForm
          member={editingMember}
          onSave={() => { setEditingMember(null); fetchTeam(); }}
          onCancel={() => setEditingMember(null)}
          auth={auth}
        />
      )}
    </div>
  );
};

// Settings Tab Component
const SettingsTab = ({ auth }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [formData, setFormData] = useState({
    resend_api_key: '',
    sender_email: '',
    contact_form_recipient: '',
    givingback_form_recipient: '',
    recaptcha_secret_key: '',
  });

  const authAxios = createAuthAxios(auth.token);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await authAxios.get('/settings');
      setSettings(response.data);
      setFormData({
        resend_api_key: '',
        sender_email: response.data.sender_email || '',
        contact_form_recipient: response.data.contact_form_recipient || '',
        givingback_form_recipient: response.data.givingback_form_recipient || '',
        recaptcha_secret_key: '',
      });
    } catch (err) {
      logger.error('Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      // Only send non-empty fields
      const updateData = {};
      if (formData.resend_api_key) updateData.resend_api_key = formData.resend_api_key;
      if (formData.sender_email) updateData.sender_email = formData.sender_email;
      if (formData.contact_form_recipient) updateData.contact_form_recipient = formData.contact_form_recipient;
      if (formData.givingback_form_recipient) updateData.givingback_form_recipient = formData.givingback_form_recipient;
      if (formData.recaptcha_secret_key) updateData.recaptcha_secret_key = formData.recaptcha_secret_key;

      if (Object.keys(updateData).length === 0) {
        setMessage({ type: 'error', text: 'No changes to save' });
        setSaving(false);
        return;
      }

      await authAxios.put('/settings', updateData);
      setMessage({ type: 'success', text: 'Settings saved successfully! Changes take effect immediately.' });
      
      // Clear API key fields after save
      setFormData(prev => ({
        ...prev,
        resend_api_key: '',
        recaptcha_secret_key: ''
      }));
      
      // Refresh settings display
      fetchSettings();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-[#4a4744]">Loading settings...</div>;
  }

  return (
    <div data-testid="settings-tab">
      <div className="mb-8">
        <p className="text-[#4a4744] text-sm">
          Configure email settings, API keys, and other site options. Changes take effect immediately without redeployment.
        </p>
      </div>

      {message && (
        <div className={`mb-6 p-4 ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-8">
        {/* Email Settings */}
        <div className="bg-white border border-[#e5e2dc] p-6">
          <h3 className="font-playfair text-lg font-bold text-[#1a1a1a] mb-4">Email Settings (Resend)</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">
                Resend API Key
              </label>
              {settings?.resend_api_key_set ? (
                <div className="flex items-center gap-3">
                  <span className="text-[#4a4744] font-mono text-sm bg-[#faf9f7] px-3 py-2 border border-[#e5e2dc]">
                    {settings.resend_api_key_masked}
                  </span>
                  <span className="text-xs text-green-600">✓ Configured {settings.resend_api_key_source === 'env' ? '(from .env)' : '(from database)'}</span>
                </div>
              ) : (
                <span className="text-red-600 text-sm">Not configured</span>
              )}
              <input
                type="password"
                value={formData.resend_api_key}
                onChange={(e) => handleChange('resend_api_key', e.target.value)}
                placeholder="Enter new API key to update..."
                className="mt-2 w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c]"
                data-testid="settings-resend-key"
              />
              <p className="text-xs text-[#4a4744] mt-1">Leave blank to keep existing key. Get your key from resend.com</p>
            </div>

            <div>
              <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">
                Sender Email Address
              </label>
              <input
                type="email"
                value={formData.sender_email}
                onChange={(e) => handleChange('sender_email', e.target.value)}
                placeholder="noreply@agilitie.com.au"
                className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c]"
                data-testid="settings-sender-email"
              />
              <p className="text-xs text-[#4a4744] mt-1">The "from" address for all emails sent by the website</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">
                  Contact Form Recipient
                </label>
                <input
                  type="email"
                  value={formData.contact_form_recipient}
                  onChange={(e) => handleChange('contact_form_recipient', e.target.value)}
                  placeholder="contact@agilitie.com.au"
                  className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c]"
                  data-testid="settings-contact-recipient"
                />
                <p className="text-xs text-[#4a4744] mt-1">Where Contact page submissions are sent</p>
              </div>

              <div>
                <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">
                  #GivingBack Form Recipient
                </label>
                <input
                  type="email"
                  value={formData.givingback_form_recipient}
                  onChange={(e) => handleChange('givingback_form_recipient', e.target.value)}
                  placeholder="contact@agilitie.com.au"
                  className="w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c]"
                  data-testid="settings-givingback-recipient"
                />
                <p className="text-xs text-[#4a4744] mt-1">Where #GivingBack interest forms are sent</p>
              </div>
            </div>
          </div>
        </div>

        {/* Security Settings */}
        <div className="bg-white border border-[#e5e2dc] p-6">
          <h3 className="font-playfair text-lg font-bold text-[#1a1a1a] mb-4">Security Settings</h3>
          
          <div>
            <label className="block text-sm font-dm-sans font-medium text-[#1a1a1a] mb-2">
              reCAPTCHA Secret Key
            </label>
            {settings?.recaptcha_secret_key_set ? (
              <div className="flex items-center gap-3">
                <span className="text-[#4a4744] font-mono text-sm bg-[#faf9f7] px-3 py-2 border border-[#e5e2dc]">
                  {settings.recaptcha_secret_key_masked}
                </span>
                <span className="text-xs text-green-600">✓ Configured {settings.recaptcha_secret_key_source === 'env' ? '(from .env)' : '(from database)'}</span>
              </div>
            ) : (
              <span className="text-red-600 text-sm">Not configured</span>
            )}
            <input
              type="password"
              value={formData.recaptcha_secret_key}
              onChange={(e) => handleChange('recaptcha_secret_key', e.target.value)}
              placeholder="Enter new secret key to update..."
              className="mt-2 w-full px-4 py-3 border border-[#e5e2dc] font-dm-sans focus:outline-none focus:border-[#d4782c]"
              data-testid="settings-recaptcha-key"
            />
            <p className="text-xs text-[#4a4744] mt-1">Google reCAPTCHA v3 secret key for form spam protection</p>
          </div>
        </div>

        <div className="flex gap-4">
          <button
            type="submit"
            disabled={saving}
            className="btn-primary flex items-center gap-2"
            data-testid="settings-save-btn"
          >
            <Save size={18} />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>

      <div className="mt-8 p-4 bg-[#faf9f7] border border-[#e5e2dc]">
        <h4 className="font-dm-sans font-semibold text-[#1a1a1a] mb-2">How it works</h4>
        <ul className="text-sm text-[#4a4744] space-y-1">
          <li>• Settings are stored in the database and take effect immediately</li>
          <li>• Database settings override .env file defaults</li>
          <li>• API keys are securely stored and never fully displayed</li>
          <li>• No redeployment needed for configuration changes</li>
        </ul>
      </div>
    </div>
  );
};

// Main Admin Page
export default function AdminPage() {
  const [auth, setAuth] = useState(null);
  const [activeTab, setActiveTab] = useState('articles');

  // Check for stored auth
  useEffect(() => {
    const stored = sessionStorage.getItem('admin_auth');
    if (stored) {
      setAuth(JSON.parse(stored));
    }
  }, []);

  const handleLogin = (credentials) => {
    sessionStorage.setItem('admin_auth', JSON.stringify(credentials));
    setAuth(credentials);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_auth');
    setAuth(null);
  };

  if (!auth) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return (
    <Layout>
      <section className="min-h-screen py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-playfair text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-2">
                Admin Panel
              </h1>
              <p className="text-[#4a4744] font-dm-sans">
                Manage articles, media, and content
              </p>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-[#4a4744] hover:text-[#1a1a1a] border border-[#e5e2dc] hover:border-[#d4782c] transition-colors">
              <LogOut size={18} /> Logout
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mb-8 border-b border-[#e5e2dc]">
            <button
              onClick={() => setActiveTab('articles')}
              className={`px-6 py-3 font-dm-sans font-medium transition-colors relative ${
                activeTab === 'articles' ? 'text-[#d4782c]' : 'text-[#4a4744] hover:text-[#1a1a1a]'
              }`}
            >
              Articles
              {activeTab === 'articles' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#d4782c]" />}
            </button>
            <button
              onClick={() => setActiveTab('media')}
              className={`px-6 py-3 font-dm-sans font-medium transition-colors relative ${
                activeTab === 'media' ? 'text-[#d4782c]' : 'text-[#4a4744] hover:text-[#1a1a1a]'
              }`}
            >
              Media & Speaking
              {activeTab === 'media' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#d4782c]" />}
            </button>
            <button
              onClick={() => setActiveTab('team')}
              className={`px-6 py-3 font-dm-sans font-medium transition-colors relative ${
                activeTab === 'team' ? 'text-[#d4782c]' : 'text-[#4a4744] hover:text-[#1a1a1a]'
              }`}
            >
              Team
              {activeTab === 'team' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#d4782c]" />}
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-6 py-3 font-dm-sans font-medium transition-colors relative ${
                activeTab === 'settings' ? 'text-[#d4782c]' : 'text-[#4a4744] hover:text-[#1a1a1a]'
              }`}
            >
              Settings
              {activeTab === 'settings' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#d4782c]" />}
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'articles' && <ArticlesTab auth={auth} />}
          {activeTab === 'media' && <MediaTab auth={auth} />}
          {activeTab === 'team' && <TeamTab auth={auth} />}
          {activeTab === 'settings' && <SettingsTab auth={auth} />}
        </div>
      </section>
    </Layout>
  );
}
